import React, {useCallback, useEffect, useMemo, useState} from 'react';
import axios from '../../services/axiosInstance';
import maCateImg from "../../assets/img/m-a-cate.png";
import TopBtn from "../../components/web/layoutsadmin/TopBtn";
import AdminSearchEvent from "../../components/web/layoutsadmin/AdminSearchEvent";
import AdminYmdEvent from "../../components/web/layoutsadmin/AdminYmdEvent";
import AdminPageEvent from "../../components/web/layoutsadmin/AdminPageEvent";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  관리자 [메인메뉴 > 결제누락]
 * ════════════════════════════════════════════════════════════════════════════════
 *  PG(페이팔/페이레터)에서는 결제가 확인되었으나(결제원장 상태 P 또는 F)
 *  비토리지 주문서/예치금에 연결되지 않은 '결제누락 의심건'을 조회한다.
 *
 *  조회 API : GET /api/payAttempt/unlinkedList  (startDate / endDate = yyyyMMdd)
 *   - 서버 조건 : status in ('P','F') AND (orderNumber is null or '')
 *
 *  [상태 의미]
 *   P(PAID)   결제는 확인됐는데 아직 주문서와 연결되지 않음  -> 즉시 확인 필요
 *   F(FAILED) 결제 후 저장까지 실패하여 사용자에게 안내된 건 -> 수동 복구 대상
 * ════════════════════════════════════════════════════════════════════════════════
 */
function AdminPayMissingPg() {

    const LCAT = localStorage.getItem('adminToken');
    const authHeader = useMemo(() => ({headers: {Authorization: LCAT}}), [LCAT]);

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [usageFilter, setUsageFilter] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [perPg, setPerPg] = useState(20);

    // ────────────────────────────────────────────────────────────────────────
    //  조회
    // ────────────────────────────────────────────────────────────────────────

    const ymd = (v) => (v ? v.replace(/-/g, '') : undefined);

    const select = useCallback(() => {
        setIsLoading(true);
        axios.get('/payAttempt/unlinkedList', {
            ...authHeader,
            params: {startDate: ymd(startDate), endDate: ymd(endDate)},
        })
            .then((res) => setData(res.data || []))
            .catch(() => setData([]))
            .finally(() => setIsLoading(false));
    }, [authHeader, startDate, endDate]);

    useEffect(() => {
        select();
    }, [select]);

    const startHandle = (e) => {
        setStartDate(e.target.value);
        setCurrentPage(1);
    };
    const endHandle = (e) => {
        setEndDate(e.target.value);
        setCurrentPage(1);
    };
    const handleSearchInputChange = (e) => {
        setSearchInput(e.target.value);
        setCurrentPage(1);
    };
    const handleSelectChange = (e) => {
        setPerPg(parseInt(e));
        setCurrentPage(1);
    };

    // ────────────────────────────────────────────────────────────────────────
    //  코드 → 문구
    // ────────────────────────────────────────────────────────────────────────

    const payWayText = (code) => ({
        'PM00': '무통장입금',
        'PM01': 'Paypal',
        'PM02': 'Wechat',
        'PM03': 'Alipay',
        'PM04': '적립금결제',
        'PM05': '예치금결제',
        'PM06': 'BtoragePay(Global)',
        'PM07': 'BtoragePay(Korean)',
        'PM08': 'GlobalCard',
        'PM09': 'KoreanPay',
    }[code] || code || '-');

    const usageText = (code) => ({
        '0': '주문서작성',
        '1': '주문서수정',
        '2': '예치금충전',
    }[code] || '-');

    const statusText = (code) => (code === 'P' ? '결제확인/미연결' : code === 'F' ? '저장실패' : code || '-');

    const comma = (v) => Number(v || 0).toLocaleString();
    const forAmountText = (v) => (v === null || v === undefined || v === '') ? '-'
        : Number(v).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});

    // ────────────────────────────────────────────────────────────────────────
    //  필터 / 페이징
    // ────────────────────────────────────────────────────────────────────────

    const filtered = useMemo(() => {
        const q = searchInput.trim().toLowerCase();
        return data.filter((d) => {
            if (statusFilter && d.status !== statusFilter) return false;
            if (usageFilter && d.usageType !== usageFilter) return false;
            if (!q) return true;
            return [d.userName, d.userId, d.payNum, d.pgTid, d.mbNum, payWayText(d.payWay), d.forCurrency]
                .filter((v) => v !== null && v !== undefined)
                .some((v) => String(v).toLowerCase().includes(q));
        });
    }, [data, searchInput, statusFilter, usageFilter]);

    const indexOfLast = currentPage * perPg;
    const indexOfFirst = indexOfLast - perPg;
    const visibleData = filtered.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.max(1, Math.ceil(filtered.length / perPg));

    /** 누락건 합계 (원화 / 통화별 외화) */
    const summary = useMemo(() => {
        let krw = 0;
        const byCurrency = {};
        filtered.forEach((d) => {
            krw += Number(d.amount || 0);
            if (d.forCurrency && d.forAmount) {
                byCurrency[d.forCurrency] = (byCurrency[d.forCurrency] || 0) + Number(d.forAmount);
            }
        });
        return {krw, byCurrency};
    }, [filtered]);

    // ────────────────────────────────────────────────────────────────────────
    //  엑셀
    // ────────────────────────────────────────────────────────────────────────

    const excelDown = async () => {
        try {
            const excel = require('exceljs');
            const workbook = new excel.Workbook();
            workbook.created = new Date();
            workbook.modified = new Date();

            const sheet = workbook.addWorksheet('결제누락');
            sheet.columns = [
                {header: '번호', key: 'no', width: 8},
                {header: '사서함번호', key: 'mbNum', width: 14},
                {header: '이름', key: 'userName', width: 16},
                {header: '이메일', key: 'userId', width: 30},
                {header: '용도', key: 'usage', width: 14},
                {header: '결제수단', key: 'payWay', width: 20},
                {header: '원화금액(KRW)', key: 'amount', width: 16},
                {header: '외화금액', key: 'forAmount', width: 14},
                {header: '통화', key: 'forCurrency', width: 10},
                {header: '결제식별번호', key: 'payNum', width: 26},
                {header: 'PG거래번호', key: 'pgTid', width: 30},
                {header: '상태', key: 'status', width: 18},
                {header: '플랫폼', key: 'platform', width: 10},
                {header: '실패사유', key: 'failReason', width: 40},
                {header: '발생일시', key: 'regTime', width: 22},
            ];

            filtered.forEach((d, i) => {
                sheet.addRow({
                    no: i + 1,
                    mbNum: d.mbNum ? `BT ${d.mbNum}` : '-',
                    userName: d.userName || '-',
                    userId: d.userId || '-',
                    usage: usageText(d.usageType),
                    payWay: payWayText(d.payWay),
                    amount: Number(d.amount || 0),
                    forAmount: d.forAmount != null ? Number(d.forAmount) : '',
                    forCurrency: d.forCurrency || '',
                    payNum: d.payNum || '',
                    pgTid: d.pgTid || '',
                    status: statusText(d.status),
                    platform: d.platform || '',
                    failReason: d.failReason || '',
                    regTime: d.regTime || '',
                });
            });
            sheet.getRow(1).font = {bold: true};

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            const now = new Date();
            const stamp = `${now.getFullYear()}${`0${now.getMonth() + 1}`.slice(-2)}${`0${now.getDate()}`.slice(-2)}`;
            anchor.href = url;
            anchor.download = `결제누락_${stamp}.xlsx`;
            anchor.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.log('엑셀 다운로드 실패 : ', e);
        }
    };

    // ────────────────────────────────────────────────────────────────────────

    return (
        <div className="AdminPayMissingPg">
            <div className="admin-work-title">
                <img className="wd-35 ht-35" alt="" src={maCateImg}/>
                <p className="p-t-6">결제누락</p>
            </div>

            <div className="admin-wrap font-t-cont">
                <div className="admin-wrap-card">

                    {/* ══ 요약 ══ */}
                    <div style={{display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "16px"}}>
                        <SumCard label="누락 건수" value={`${comma(filtered.length)} 건`} tone="#F09C9C"/>
                        <SumCard label="원화 합계" value={`${comma(summary.krw)} KRW`} tone="#7C9CF5"/>
                        <SumCard label="외화 합계"
                                 value={Object.keys(summary.byCurrency).length === 0 ? '-' :
                                     Object.entries(summary.byCurrency)
                                         .map(([cur, amt]) => `${forAmountText(amt)} ${cur}`).join('  /  ')}
                                 tone="#FFC978"/>
                    </div>

                    {/* ══ 조회조건 ══ */}
                    <div className="admin-event-wrap">
                        <div className="admin-event-wrap-btn">
                            <AdminPageEvent perPg={perPg} handleSelectChange={handleSelectChange}/>
                            <AdminYmdEvent startDate={startDate} endDate={endDate}
                                           startHandle={startHandle} endHandle={endHandle}/>
                            <div style={{marginRight: "15px"}}>
                                <select className="admin-combo wd-140 ht-30 p-l-7 font-s-14 font-t-cont"
                                        value={statusFilter}
                                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
                                    <option value="">전체상태</option>
                                    <option value="P">결제확인/미연결</option>
                                    <option value="F">저장실패</option>
                                </select>
                            </div>
                            <div style={{marginRight: "15px"}}>
                                <select className="admin-combo wd-120 ht-30 p-l-7 font-s-14 font-t-cont"
                                        value={usageFilter}
                                        onChange={(e) => { setUsageFilter(e.target.value); setCurrentPage(1); }}>
                                    <option value="">전체용도</option>
                                    <option value="0">주문서작성</option>
                                    <option value="1">주문서수정</option>
                                    <option value="2">예치금충전</option>
                                </select>
                            </div>
                            <AdminSearchEvent searchInput={searchInput}
                                              handleSearchInputChange={handleSearchInputChange}/>
                        </div>
                        <div className="admin-event-wrap-btn gap10">
                            <button className="admin-btn-B wd-100 ht-30 font-s-13 btn-click-event"
                                    onClick={select}>새로고침
                            </button>
                            <button className="admin-btn-A wd-100 ht-30 font-s-13 btn-click-event"
                                    onClick={excelDown}>EXCEL
                            </button>
                        </div>
                    </div>

                    {/* ══ 목록 ══ */}
                    <div className="div-tb m-b-10">
                        <div className="div-tb-head ht-60">
                            <div style={{width: "50px"}}><p>번호</p></div>
                            <div style={{width: "90px"}}><p>사서함번호</p></div>
                            <div style={{width: "120px"}}><p>이름</p></div>
                            <div style={{width: "220px"}}><p>이메일</p></div>
                            <div style={{width: "100px"}}><p>용도</p></div>
                            <div style={{width: "150px"}}><p>결제수단</p></div>
                            <div style={{width: "130px"}}><p>원화금액(&#8361;)</p></div>
                            <div style={{width: "110px"}}><p>외화금액</p></div>
                            <div style={{width: "70px"}}><p>통화</p></div>
                            <div style={{width: "190px"}}><p>결제식별번호</p></div>
                            <div style={{width: "220px"}}><p>PG거래번호</p></div>
                            <div style={{width: "130px"}}><p>상태</p></div>
                            <div style={{width: "80px"}}><p>플랫폼</p></div>
                            <div style={{width: "150px"}}><p>발생일시</p></div>
                        </div>

                        {visibleData.length === 0 ?
                            <div className="div-tb-body ht-70">
                                <div className="wd-100pc text-center">
                                    <p>{isLoading ? '조회중입니다' : '결제누락 건이 없습니다'}</p>
                                </div>
                            </div>
                            :
                            visibleData.map((d, i) => (
                                <div key={d.seqNum} className="div-tb-body ht-70"
                                     title={d.failReason || ''}>
                                    <div style={{width: "50px"}}><p>{indexOfFirst + i + 1}</p></div>
                                    <div style={{width: "90px"}}><p>{d.mbNum ? `BT ${d.mbNum}` : '-'}</p></div>
                                    <div style={{width: "120px"}}><p className="font-b">{d.userName || '-'}</p></div>
                                    <div style={{width: "220px"}} className="admin-tb-text-cut"><p>{d.userId || '-'}</p></div>
                                    <div style={{width: "100px"}}><p>{usageText(d.usageType)}</p></div>
                                    <div style={{width: "150px"}}><p>{payWayText(d.payWay)}</p></div>
                                    <div style={{width: "130px"}}><p className="font-b">{comma(d.amount)}</p></div>
                                    <div style={{width: "110px"}}><p>{forAmountText(d.forAmount)}</p></div>
                                    <div style={{width: "70px"}}><p>{d.forCurrency || '-'}</p></div>
                                    <div style={{width: "190px"}} className="admin-tb-text-cut"><p>{d.payNum || '-'}</p></div>
                                    <div style={{width: "220px"}} className="admin-tb-text-cut"><p>{d.pgTid || '-'}</p></div>
                                    <div style={{width: "130px"}}>
                                        <StatusPill status={d.status} text={statusText(d.status)}/>
                                    </div>
                                    <div style={{width: "80px"}}><p>{d.platform || '-'}</p></div>
                                    <div style={{width: "150px"}}><p>{d.regTime || '-'}</p></div>
                                </div>
                            ))
                        }
                    </div>

                    {/* ══ 페이징 ══ */}
                    {totalPages > 1 &&
                        <div style={{display: "flex", justifyContent: "center", alignItems: "center", gap: "6px", padding: "10px 0"}}>
                            <button className="admin-btn-C wd-60 ht-28 font-s-12 btn-click-event"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>이전
                            </button>
                            <span className="font-s-13" style={{padding: "0 10px"}}>{currentPage} / {totalPages}</span>
                            <button className="admin-btn-C wd-60 ht-28 font-s-12 btn-click-event"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>다음
                            </button>
                        </div>
                    }

                    <p className="font-s-12" style={{margin: "4px 2px 0", color: "#9A9A9A"}}>
                        ※ PG에서 결제는 확인되었으나 주문서/예치금에 연결되지 않은 건입니다. 행에 마우스를 올리면 실패사유가 표시됩니다.
                    </p>
                </div>
            </div>

            <TopBtn/>
        </div>
    );
}

/** 상단 합계 카드 */
function SumCard({label, value, tone}) {
    return (
        <div style={{
            flex: "1 1 200px", minWidth: "200px", borderRadius: "14px", padding: "16px 18px",
            border: "1px solid #EDEDED", background: "#FFFFFF",
            boxShadow: "0 6px 18px -14px rgba(0,0,0,0.35)",
        }}>
            <p style={{margin: "0 0 5px", fontSize: "12px", color: "#9A9A9A"}}>{label}</p>
            <p style={{margin: 0, fontSize: "17px", fontWeight: "bold", color: tone, wordBreak: "break-all"}}>{value}</p>
        </div>
    );
}

/** 상태 뱃지 (P=주의 / F=위험) */
function StatusPill({status, text}) {
    const danger = status === 'F';
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            padding: "5px 11px", borderRadius: "999px",
            background: danger ? "#FDECEC" : "#FFF4E2",
            border: `1px solid ${danger ? "#F6C9C9" : "#FBDDA9"}`,
            color: danger ? "#D06B6B" : "#C98A2E",
            fontSize: "12px", fontWeight: "bold", whiteSpace: "nowrap",
        }}>
            <span style={{width: "6px", height: "6px", borderRadius: "50%", background: danger ? "#F09C9C" : "#FFC978"}}/>
            {text}
        </span>
    );
}

export default AdminPayMissingPg;
